import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { useRecoilValue } from 'recoil';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { useHippoText } from '../../../../hooks/useHippoText';
import { Link, NumberField } from '@flos/react-ui';
import ViewInformationModal from '../../../../shared_components/ViewInformationModal/ViewInformationModal';
import { useState } from 'react';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { track } from '../../../../utils/trackingUtil';
import { FLAVOUR_NAME } from '../../../../constants/coverable';
import { currentOnsaCoverableSelector } from '../../../../recoil/selectors/currentOnsaCoverableSelector';
import type {
    CaravanOnsaDTO,
    TrailerOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/uvp';
import { getAgeOfTheOldestFromCoverableList } from '../../../../utils/coverablesUtil';

const CurrentValueOfUvpStep = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): React.JSX.Element => {
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const getHippoText = useHippoText();
    const coverable = useRecoilValue<CaravanOnsaDTO | TrailerOnsaDTO>(currentOnsaCoverableSelector);
    const { flavourName } = coverable;
    const isCaravan = flavourName === FLAVOUR_NAME.UVP_CARAVAN;
    const flavourHippoKey = isCaravan ? 'CARAVAN' : 'TRAILER';
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [currentValue, setCurrentValue] = useState<number | undefined>(coverable?.currentValue);

    const onNext = (): void => {
        setIsLoading(true);

        const existingAgeOfTheOldest = getAgeOfTheOldestFromCoverableList(coverables);

        updateCoverableInformation({
            ...coverable,
            currentValue,
            ageOfOldestInHousehold: existingAgeOfTheOldest
        });
        const updatedCoverable: CoverableInformationToUpdateDTO = {
            [isCaravan ? 'caravan' : 'trailer']: {
                publicId: coverable?.publicId ?? '',
                fixedId: coverable?.fixedId ?? '',
                currentValueEstimation: currentValue,
                ageOfOldestInHousehold: existingAgeOfTheOldest
            },
            productToUpdate: flavourName
        };
        void saveCoverableInformation(updatedCoverable).then(() => {
            track(`${flavourName}CoverableInformationCurrentValue`, [
                {
                    currentValueEstimation: currentValue
                }
            ]);
            setIsLoading(false);
            goNext();
        });
    };

    return (
        <WizardPagetemplate
            onNext={onNext}
            onPrevious={goPrevious}
            disableNext={isLoading || !currentValue}
            isNextLoading={isLoading}
        >
            <CoverableDetailsTemplate
                header={getHippoText(
                    `DSS-COVERABLE-INFORMATION-STEP-${flavourHippoKey}-CURRENT-VALUE-HEADER`
                )}
                subheader={getHippoText(
                    `DSS-COVERABLE-INFORMATION-STEP-${flavourHippoKey}-CURRENT-VALUE-SUBTITLE`
                )}
            >
                <NumberField
                    label={getHippoText('DSS-COVERABLE-INFORMATION-STEP-CURRENT-VALUE-INPUT')}
                    value={currentValue?.toString()}
                    pattern="[0-9]+"
                    onChange={(event) => {
                        setCurrentValue(parseInt(event.target.value));
                    }}
                    onBlur={(event): void => {
                        updateCoverableInformation({
                            currentValue: parseInt(event.target.value)
                        });
                    }}
                    minLength={1}
                    disabled={isLoading}
                />
                <div className="u-text-right">
                    <Link
                        className="u-spacing-stack-top-6"
                        onClick={() => {
                            setShowModal(true);
                        }}
                        theme="logo"
                    >
                        {getHippoText(
                            'DSS-COVERABLE-INFORMATION-STEP-CURRENT-VALUE-WHAT-DOES-CURRENT-VALUE-MEAN'
                        )}
                    </Link>
                </div>
            </CoverableDetailsTemplate>
            {showModal && (
                <ViewInformationModal
                    closeModal={() => {
                        setShowModal(false);
                    }}
                    showModal={showModal}
                    modalProps={{
                        title: getHippoText('DSS-INFO-MODAL-UVPLOB-CURRENT-VALUE-TITLE'),
                        description: '',
                        rawHtmlDescription: getHippoText(
                            'DSS-INFO-MODAL-UVPLOB-CURRENT-VALUE-BODY',
                            {
                                variant: isCaravan ? 'campingvogn' : 'påhængsvogn'
                            }
                        )
                    }}
                />
            )}
        </WizardPagetemplate>
    );
};

export default CurrentValueOfUvpStep;
